import { http } from '@/shared/api/axios';
import type {
  CreateEmployeeRequest,
  CreateEmployeeResponse,
  EmployeeFilters,
  EmployeeLookup,
  EmployeeProfileResponse,
  MyProfileResponse,
  PagedEmployeeResponse,
  UpdateProfileRequest,
  UpdateEmployeeRequest,
  UpdateUserRequest,
  SendInvitationRequest,
  SendInvitationResponse,
  InvitationUserLookupResponse,
  AccountInvitation,
  GetAccountInvitationsRequest,
  UpdateInvitationRoleRequest,
  BulkAssignDepartmentRequest,
  BulkAssignDepartmentResponse,
  BulkAssignEmploymentCategoryRequest,
  BulkAssignEmploymentCategoryResponse,
  ChangeEmployeeTypeRequest,
  EndEmploymentRequest,
  EndEmploymentResult,
  EditEndRequest,
  EndPreflightParams,
  EndPreflightResponse,
  ExitSummary,
  TickExitChecklistItemRequest,
  EmploymentPeriod,
  RehireRequest,
  RehireResult,
  ActivateResult,
  RecordExitDetailsRequest,
} from '../model/types';
import type { PaginatedResponse } from '@shared/api/types';

export const fetchEmployees = async (filters: EmployeeFilters): Promise<PagedEmployeeResponse> => {
  const response = await http.get<PagedEmployeeResponse>('/v1/employees', {
    params: {
      PageNumber: filters.pageNumber,
      PageSize: filters.pageSize,
      SortDescending: filters.sortDescending,
      ...(filters.searchTerm && { SearchTerm: filters.searchTerm }),
      ...(filters.employeeType && { EmployeeType: filters.employeeType }),
      ...(filters.isActive !== undefined && { IsActive: filters.isActive }),
      ...(filters.state && { State: filters.state }),
      ...(filters.departmentIds && filters.departmentIds.length > 0 && {
        DepartmentIds: filters.departmentIds,
      }),
      ...(filters.employmentCategoryIds && filters.employmentCategoryIds.length > 0 && {
        EmploymentCategoryIds: filters.employmentCategoryIds,
      }),
      ...(filters.exitChecklistOpen !== undefined && { ExitChecklistOpen: filters.exitChecklistOpen }),
      ...(filters.rehireEligible !== undefined && { RehireEligible: filters.rehireEligible }),
      ...(filters.sortBy && { SortBy: filters.sortBy }),
    },
    paramsSerializer: { indexes: null }, // serializes arrays as DepartmentIds=1&DepartmentIds=2
  });
  return response.data;
};

/**
 * Create a new employee
 * POST /api/v1/employees
 */
export const createEmployee = async (data: CreateEmployeeRequest): Promise<CreateEmployeeResponse> => {
  const response = await http.post<CreateEmployeeResponse>('/v1/employees', data);
  return response.data;
};

export const fetchEmployeeLookup = async (ids?: number[]): Promise<EmployeeLookup[]> => {
  const response = await http.get<EmployeeLookup[]>('/v1/lookup/employee/backoffice', {
    params: ids && ids.length > 0 ? { ids: ids.join(',') } : undefined,
  });
  return response.data;
};

export const fetchGuardLookup = async (
  ids?: number[],
  clientId?: number,
  options?: { skipErrorToast?: boolean; includeEnded?: boolean },
): Promise<EmployeeLookup[]> => {
  const response = await http.get<EmployeeLookup[]>('/v1/lookup/employee/guards', {
    params: {
      ...(ids && ids.length > 0 ? { ids: ids.join(',') } : {}),
      ...(clientId != null ? { clientId } : {}),
      ...(options?.includeEnded ? { includeEnded: true } : {}),
    },
    ...(options?.skipErrorToast ? { _skipErrorToast: true } : {}),
  } as Parameters<typeof http.get>[1]);
  return response.data;
};

export const fetchEmployeeProfile = async (employeeId: number): Promise<EmployeeProfileResponse> => {
  const response = await http.get(`/v1/employees/${employeeId}`);
  return response.data;
};

/**
 * Get current user's employee profile
 * Returns personal info + employments[] array (multi-company support)
 */
export const fetchCurrentEmployeeProfile = async (): Promise<MyProfileResponse> => {
  const response = await http.get<MyProfileResponse>('/v1/employees/me');
  return response.data;
};

const buildProfileFormData = (data: UpdateProfileRequest): FormData => {
  const formData = new FormData();

  if (data.firstName !== undefined) formData.append('FirstName', data.firstName);
  if (data.lastName !== undefined) formData.append('LastName', data.lastName);
  if (data.dateOfBirth !== undefined && data.dateOfBirth !== null) {
    formData.append('DateOfBirth', data.dateOfBirth);
  }
  if (data.gender !== undefined && data.gender !== null) {
    formData.append('Gender', data.gender);
  }
  if (data.address !== undefined && data.address !== null) {
    formData.append('Address', data.address);
  }
  if (data.emergencyContactName !== undefined && data.emergencyContactName !== null) {
    formData.append('EmergencyContactName', data.emergencyContactName);
  }
  if (data.emergencyContactPhone !== undefined && data.emergencyContactPhone !== null) {
    formData.append('EmergencyContactPhone', data.emergencyContactPhone);
  }
  if (data.profileImage) {
    formData.append('ProfileImage', data.profileImage);
  }

  return formData;
};

/**
 * Update current user's profile (personal information)
 * PATCH /api/v1/profiles/me
 */
export const updateCurrentProfile = async (
  data: UpdateProfileRequest
): Promise<void> => {
  const formData = buildProfileFormData(data);
  await http.patch('/v1/profiles/me', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * Update employee record (job details)
 * PATCH /api/v1/employees/{id}
 */
export const updateEmployee = async (
  employeeId: number,
  data: UpdateEmployeeRequest
): Promise<void> => {
  await http.patch(`/v1/employees/${employeeId}`, data);
};

/**
 * Update the current user's account info (email, mobile)
 * PATCH /api/v1/users/me
 */
export const updateCurrentUser = async (data: UpdateUserRequest): Promise<void> => {
  await http.patch('/v1/users/me', data);
};

// ========================================
// Employee Status API
// ========================================

/**
 * 204 on success. Refused with 402 (plan's user limit reached) or 422 (already active), and the caller
 * explains both, so the client's generic toast is skipped.
 */
export const activateEmployee = async (employeeId: number): Promise<ActivateResult> => {
  const response = await http.post<ActivateResult | ''>(`/v1/employees/${employeeId}/activate`, undefined, {
    _skipErrorToast: true,
  } as never);
  return response.data || {};
};

export const deactivateEmployee = async (
  employeeId: number,
  data: { inactiveReason: string }
): Promise<void> => {
  await http.post(`/v1/employees/${employeeId}/deactivate`, data);
};

export const withdrawEmployee = async (employeeId: number, data: { reason: string }): Promise<void> => {
  await http.post(`/v1/employees/${employeeId}/withdraw`, data, { _skipErrorToast: true } as never);
};

export const endEmployment = async (data: EndEmploymentRequest): Promise<EndEmploymentResult> => {
  const response = await http.post<EndEmploymentResult>('/v1/employees/end-employment', data, {
    _skipErrorToast: true,
  } as never);
  return response.data;
};

export const fetchEndPreflight = async (params: EndPreflightParams): Promise<EndPreflightResponse> => {
  const response = await http.get<EndPreflightResponse>('/v1/employees/end-employment/preflight', {
    params: {
      ids: params.ids,
      endAccessNow: params.endAccessNow,
      ...(params.lastDayFromAttendance ? { lastDayFromAttendance: true } : { lastDay: params.lastDay }),
    },
    paramsSerializer: { indexes: null },
    _skipErrorToast: true,
  } as never);
  return response.data;
};

export const editEmploymentEnd = async (employeeId: number, data: EditEndRequest): Promise<void> => {
  await http.put(`/v1/employees/${employeeId}/end-employment`, data, { _skipErrorToast: true } as never);
};

export const cancelEmploymentEnd = async (employeeId: number): Promise<void> => {
  await http.delete(`/v1/employees/${employeeId}/end-employment`, { _skipErrorToast: true } as never);
};

export const suspendEmployee = async (employeeId: number, data: { note: string }): Promise<void> => {
  await http.post(`/v1/employees/${employeeId}/suspend`, data, { _skipErrorToast: true } as never);
};

export const liftEmployeeSuspension = async (employeeId: number): Promise<void> => {
  await http.post(`/v1/employees/${employeeId}/lift-suspension`, undefined, { _skipErrorToast: true } as never);
};

export const fetchExitSummary = async (employeeId: number): Promise<ExitSummary> => {
  const response = await http.get<ExitSummary>(`/v1/employees/${employeeId}/exit-summary`, {
    _skipErrorToast: true,
  } as never);
  return response.data;
};

export const tickExitChecklistItem = async (
  employeeId: number,
  key: string,
  data: TickExitChecklistItemRequest,
): Promise<void> => {
  await http.put(`/v1/employees/${employeeId}/exit-checklist/${encodeURIComponent(key)}`, data, {
    _skipErrorToast: true,
  } as never);
};

export const fetchEmploymentHistory = async (employeeId: number): Promise<EmploymentPeriod[]> => {
  const response = await http.get<EmploymentPeriod[]>(`/v1/employees/${employeeId}/employment-history`, {
    _skipErrorToast: true,
  } as never);
  return Array.isArray(response.data) ? response.data : [];
};

export const rehireEmployees = async (data: RehireRequest): Promise<RehireResult> => {
  const response = await http.post<RehireResult>('/v1/employees/rehire', data, { _skipErrorToast: true } as never);
  return response.data ?? {};
};

export const recordExitDetails = async (employeeId: number, data: RecordExitDetailsRequest): Promise<void> => {
  await http.post(`/v1/employees/${employeeId}/exit-details`, data, { _skipErrorToast: true } as never);
};

// ========================================
// Employee Credentials API
// ========================================

/**
 * Get credentials for an employee
 * GET /api/v1/employees/{employeeId}/credentials
 */
export const fetchEmployeeCredentials = async (employeeId: number): Promise<import('../model/types').EmployeeCredential[]> => {
  const response = await http.get(`/v1/employees/${employeeId}/credentials`);
  return response.data;
};

/**
 * Get credentials for the current user
 * GET /api/v1/employees/me/credentials
 */
export const fetchCurrentEmployeeCredentials = async (): Promise<import('../model/types').EmployeeCredential[]> => {
  const response = await http.get('/v1/employees/me/credentials');
  return response.data;
};

const buildCredentialFormData = (data: import('../model/types').CreateCredentialRequest): FormData => {
  const formData = new FormData();
  formData.append('CredentialTypeId', data.CredentialTypeId.toString());
  if (data.CredentialNumber) formData.append('CredentialNumber', data.CredentialNumber);
  if (data.IssuingAuthority) formData.append('IssuingAuthority', data.IssuingAuthority);
  if (data.IssuedOn) formData.append('IssuedOn', data.IssuedOn);
  if (data.ExpiresOn) formData.append('ExpiresOn', data.ExpiresOn);
  if (data.Notes) formData.append('Notes', data.Notes);
  if (data.Document) formData.append('Document', data.Document);
  return formData;
};

/**
 * Create a new credential for an employee
 * POST /api/v1/employees/{employeeId}/credentials
 */
export const createEmployeeCredential = async (
  employeeId: number,
  data: import('../model/types').CreateCredentialRequest
): Promise<void> => {
  await http.post(`/v1/employees/${employeeId}/credentials`, buildCredentialFormData(data), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * Create a new credential for the current user
 * POST /api/v1/employees/me/credentials
 */
export const createCurrentEmployeeCredential = async (
  data: import('../model/types').CreateCredentialRequest
): Promise<void> => {
  await http.post('/v1/employees/me/credentials', buildCredentialFormData(data), {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

/**
 * Delete the current user's credential
 * DELETE /api/v1/employee/credentials/me/{credentialId}
 */
export const deleteCurrentEmployeeCredential = async (credentialId: number): Promise<void> => {
  await http.delete(`/v1/employee/credentials/me/${credentialId}`);
};

/**
 * Verify a credential
 * POST /api/v1/employee/credentials/{credentialId}/verify
 */
export const verifyEmployeeCredential = async (
  credentialId: number,
  data: import('../model/types').VerifyCredentialRequest
): Promise<void> => {
  await http.post(`/v1/employee/credentials/${credentialId}/verify`, data);
};

/**
 * Reject a credential
 * POST /api/v1/employee/credentials/{credentialId}/reject
 */
export const rejectEmployeeCredential = async (
  credentialId: number,
  data: import('../model/types').RejectCredentialRequest
): Promise<void> => {
  await http.post(`/v1/employee/credentials/${credentialId}/reject`, data);
};

// ========================================
// Employment Invitations API
// ========================================

/**
 * Send a guard employment invitation
 * POST /api/v1/employment-invitations
 */
export const sendInvitation = async (data: SendInvitationRequest): Promise<SendInvitationResponse> => {
  const response = await http.post<SendInvitationResponse>('/v1/employment-invitations', data);
  return response.data;
};

/**
 * Look up a user by email for invitation flow.
 * Returns null when the user is not found (404 → null) so callers can distinguish "doesn't exist" from "errored".
 * GET /api/v1/employment-invitations/user-lookup
 */
export const lookupInvitationUserByEmail = async (
  email: string
): Promise<InvitationUserLookupResponse | null> => {
  try {
    const response = await http.get<InvitationUserLookupResponse>(
      '/v1/employment-invitations/user-lookup',
      { params: { email }, _skipErrorToast: true } as Parameters<typeof http.get>[1]
    );
    return response.data;
  } catch (error: unknown) {
    const e = error as { response?: { status: number } };
    if (e.response?.status === 404) return null;
    throw error;
  }
};

/**
 * Paginated list of invitations this account has sent.
 * GET /api/v1/employment-invitations
 */
export const getAccountInvitations = async (
  params: GetAccountInvitationsRequest
): Promise<PaginatedResponse<AccountInvitation>> => {
  const response = await http.get<PaginatedResponse<AccountInvitation>>(
    '/v1/employment-invitations',
    { params }
  );
  return response.data;
};

/**
 * Re-fire the notification for a pending invitation.
 * POST /api/v1/employment-invitations/{id}/resend
 */
export const resendInvitation = async (id: number): Promise<void> => {
  await http.post(`/v1/employment-invitations/${id}/resend`);
};

/**
 * Cancel (revoke) a pending invitation.
 * DELETE /api/v1/employment-invitations/{id}
 */
export const cancelInvitation = async (id: number): Promise<void> => {
  await http.delete(`/v1/employment-invitations/${id}`);
};

/**
 * Change the role on a pending invitation.
 * PATCH /api/v1/employment-invitations/{id}/role
 */
export const updateInvitationRole = async (
  id: number,
  data: UpdateInvitationRoleRequest
): Promise<void> => {
  await http.patch(`/v1/employment-invitations/${id}/role`, data);
};

// ========================================
// Bulk-assign API
// ========================================

export const bulkAssignDepartment = async (
  data: BulkAssignDepartmentRequest
): Promise<BulkAssignDepartmentResponse> => {
  const response = await http.patch<BulkAssignDepartmentResponse>(
    '/v1/employees/bulk/department',
    data
  );
  return response.data;
};

export const bulkAssignEmploymentCategory = async (
  data: BulkAssignEmploymentCategoryRequest
): Promise<BulkAssignEmploymentCategoryResponse> => {
  const response = await http.patch<BulkAssignEmploymentCategoryResponse>(
    '/v1/employees/bulk/employment-category',
    data
  );
  return response.data;
};

// ========================================
// Change Employee Type (promote)
// ========================================

export const changeEmployeeType = async (
  employeeId: number,
  data: ChangeEmployeeTypeRequest
): Promise<void> => {
  await http.patch(`/v1/employees/${employeeId}/promote`, data);
};

